/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CardMetadata, ProductCard, Typography2 as Typography, breakpoints, typography2 } from '@coursera/cds-core';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import ProductCardRatings from 'bundles/product-card/components/ProductCardRatings';
import {
  capitalizeAndTranslateEachWordInEntityType,
  eventingV3ProductType,
  extractEventId,
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { MIN_RATING_COUNT } from 'bundles/product-card/constants';

type Props = {
  entity: CollectionEntity;
  entityIndex: number;
  additionalV3TrackingData?: Record<string, unknown>;
};

const PRODUCT_CARD_IMAGE_HEIGHT = 64;

const styles = {
  cardContainer: css`
    height: 100%;

    /* CDS ProductCard style overrides */
    .cds-ProductCard-list {
      height: 100%;
      min-width: unset !important;
      padding: calc(var(--cds-spacing-50) / 2 * 5);
      border-radius: var(--cds-border-radius-100);

      .cds-CommonCard-clickArea {
        height: 100%;
      }

      .cds-ProductCard-listCard {
        height: 100%;
      }

      .cds-ProductCard-listContentContainer {
        height: 100%;
        flex-direction: row !important;
      }

      .cds-ProductCard-listPreviewImage {
        height: 64px;
        width: 64px;

        img {
          height: 64px;
          width: 64px;
        }
      }

      .cds-ProductCard-content {
        gap: calc(var(--cds-spacing-50) / 2);
      }

      .cds-ProductCard-header {
        gap: var(--cds-spacing-50);
      }

      .cds-CommonCard-title {
        -webkit-line-clamp: 1;
        ${typography2.subtitleSmall};

        ${breakpoints.down('sm')} {
          -webkit-line-clamp: 2;
        }
      }

      .cds-Avatar-base {
        width: 12px !important;
        height: 12px !important;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .cds-CommonCard-metadata > p {
        ${typography2.bodyTertiary}
      }
    }
  `,
  cardFooter: css`
    display: flex;
    gap: var(--cds-spacing-100);
    align-items: center;
  `,
};

const CompactCollectionProductCard = ({ entity, entityIndex, additionalV3TrackingData }: Props) => {
  const track = useTracker();
  const trackingData: EventData<'click_product_card'> = {
    productCard: { index: entityIndex },
    product: {
      id: extractEventId(entity.id) as string,
      name: entity.name,
      slug: entity.slug,
      type: eventingV3ProductType(entity.productType),
    },
    ...additionalV3TrackingData,
  };

  const trackCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  const getProductCardImageProps = () => ({ height: PRODUCT_CARD_IMAGE_HEIGHT });

  return (
    <div css={styles.cardContainer} ref={trackCardRef}>
      <ProductCard
        alwaysSmallImage
        variant="list"
        onClick={() => {
          track('click_product_card', trackingData);
        }}
        partners={
          entity.partners
            ? getPartnerNamesAndLogos(entity.partners, !!entity?.coBrandingEnabled, entity.partnerLogos)
            : []
        }
        productType={entity.productType as string}
        renderPreviewImage={() => (
          <LearningProductCardImage
            imageUrl={entity.imageUrl}
            imageParams={{
              ...getProductCardImageProps,
              ...getImagePropOverrides(entity.partners ?? [], entity.imageUrl ?? ''),
            }}
          />
        )}
        title={{
          name: entity.name,
          linkProps: {
            href: entity.link,
          },
        }}
        footer={
          <div css={styles.cardFooter}>
            {entity?.productType && (
              <CardMetadata
                metadata={[capitalizeAndTranslateEachWordInEntityType(entity.productType)].filter(Boolean)}
              />
            )}
            {!!entity?.avgProductRating &&
              !!entity?.numProductRatings &&
              entity?.numProductRatings > MIN_RATING_COUNT && (
                <>
                  <Typography variant="bodySecondary" component="span" color="supportText">
                    ·
                  </Typography>
                  <ProductCardRatings
                    avgProductRating={entity.avgProductRating}
                    numProductRatings={entity.numProductRatings}
                  />
                </>
              )}
          </div>
        }
      />
    </div>
  );
};

export default CompactCollectionProductCard;
